import type { GridApi, ICellRendererParams, IRowNode } from 'ag-grid-community';
import type { ComponentType, ReactNode } from 'react';

import type { BlotterTableProps } from '../types';
import type { RenderCellOptions } from '../useRenderCell';

/**
 * Props passed to the consumer-supplied card renderer for each leaf row.
 *
 * Consumers should not touch ag-grid internals directly — `renderCell` is the
 * bridge that mounts the existing column renderer for any column ID, so things
 * like sentiment colouring, currency formatting, and increment handling stay
 * consistent with the desktop blotter without re-implementation.
 */
export interface MobileCardRendererProps<TRow> {
  /** The row's underlying data object. */
  data: TRow;
  /** ag-grid row node — exposed for advanced uses (selection, expansion, etc.). */
  node: IRowNode<TRow>;
  /** ag-grid api — exposed for iterating columns or imperative actions. */
  api: GridApi<TRow>;
  /** Render the cell for the given column ID using the same renderer the desktop grid uses. */
  renderCell: (colId: string, options?: RenderCellOptions) => ReactNode;
  /** Optional row press action injected by `MobileDataTable`. */
  onPress?: () => void;
  /** Optional flag to render a compact card. */
  compact?: boolean;
}

export interface MobileRowPressParams<TRow> {
  data: TRow;
  node: IRowNode<TRow>;
  api: GridApi<TRow>;
  renderCell: (colId: string, options?: RenderCellOptions) => ReactNode;
}

/**
 * Props passed to the consumer-supplied group row renderer. Extends ag-grid's
 * standard {@link ICellRendererParams} with the same `renderCell` bridge the
 * leaf cards use — for group rows it mounts the column renderer against the
 * group node, so aggregated values render with the column's existing
 * formatter (currency tagging, sentiment colour, etc.) without bespoke logic.
 */
export interface MobileGroupCardRendererProps<TRow = any> extends Omit<ICellRendererParams<TRow>, 'getValue'> {
  /** Render the cell for the given column ID using the column's existing renderer. */
  renderCell: (colId: string, options?: RenderCellOptions) => ReactNode;
}

export type MobileDataTableProps<TRow> = BlotterTableProps<TRow> & {
  /**
   * Component used to render each data row as a card. Receives `renderCell` so
   * it can embed existing ag-grid renderers without knowing about column types.
   */
  cardRenderer: ComponentType<MobileCardRendererProps<TRow>>;

  /**
   * Extra props forwarded to `cardRenderer` (for example `compact`).
   * Does not override `data`, `node`, `api`, `renderCell`, or `onPress`.
   */
  cardRendererProps?: Omit<Partial<MobileCardRendererProps<TRow>>, 'data' | 'node' | 'api' | 'renderCell' | 'onPress'>;

  /** Optional extra cell renderer components, merged into the default registry. */
  extraComponents?: Record<string, any>;

  /** Optional fixed card height. Defaults to {@link MOBILE_CARD_HEIGHT}. */
  cardHeight?: number;

  /**
   * Optional height for group rows. Defaults to {@link MOBILE_GROUP_CARD_HEIGHT}
   * — shorter than expanded `cardHeight` so group summaries don't waste vertical space.
   * The table uses {@link resolveMobileGroupCardHeight}, so compact cards shorter
   * than this value shrink the group row to match.
   */
  groupCardHeight?: number;

  /** Optional title for the details sheet. Defaults to 'Details'. */
  detailsTitle?: string;

  /** Optional bottom padding to compensate for the page footer. Defaults to {@link BOTTOM_PADDING}. */
  bottomPadding?: number;

  /** Optional top padding to compensate for the page header. Defaults to {@link MOBILE_HEADER_HEIGHT}. */
  topPadding?: number;

  /**
   * Optional handler invoked when a leaf row/card is pressed. Useful for opening
   * a details sheet at the table level instead of coupling card components to
   * modal logic.
   *
   * When omitted the default behaviour opens a {@link MobileRowDetailSheet}
   * via the nearest {@link MobileModalProvider}.
   */
  onRowPress?: (params: MobileRowPressParams<TRow>) => void;

  /**
   * Optional render function for sticky footer content inside the default
   * detail sheet (e.g. action buttons). Receives the latest row data.
   *
   * Ignored when `onRowPress` is provided (the custom handler controls the
   * full sheet rendering in that case).
   */
  rowDetailFooter?: (data: TRow) => ReactNode;

  /**
   * Optional content rendered above the default detail rows. Receives the latest row data.
   */
  rowDetailHeader?: (data: TRow) => ReactNode;

  /**
   * Optional group row renderer. When provided, replaces the default
   * {@link MobileGroupCard}. Receives the standard ag-grid cell renderer
   * params plus a `renderCell` helper that mounts the column's renderer
   * against the group node — the same bridge {@link MobileCardRendererProps}
   * exposes to leaf cards. Multi-level group expansion is preserved: ag-grid
   * mounts the renderer for every level with the appropriate `node.level`
   * and `node.field`.
   */
  groupCardRenderer?: ComponentType<MobileGroupCardRendererProps<TRow>>;
};

export const MOBILE_CARD_HEIGHT = 96;
export const MOBILE_GROUP_CARD_HEIGHT = 64;

/** Group rows are never taller than leaf rows, so compact cards shrink groups with them. */
export function resolveMobileGroupCardHeight(cardHeight: number, groupCardHeight = MOBILE_GROUP_CARD_HEIGHT): number {
  return Math.min(cardHeight, groupCardHeight);
}
